import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { API_BASE_URL } from '@/constants/api';
import { authService } from './auth';
import { autoAddAuthIfRequired } from './interceptors';

// Types for API responses
export interface ApiResponse<T = any> {
  properties?: never[];
  data?: T;
  status?: number;
  message?: string;
  success?: boolean;
  [key: string]: any; // Allow additional properties
}

export interface PaginatedResponse<T = any> extends ApiResponse<T[]> {
  pagination: {
    page: number;
    page_size: number;
    total: number;
    total_pages: number;
  };
}

export interface ErrorResponse {
  message: string;
  status: number;
  errors?: Record<string, string[]>;
}

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000, // 30 seconds
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Response interceptor - handle common responses
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        // Log successful response for debugging
        return response;
      },
      async (error) => {
        const originalRequest = error.config;
        
        // Handle 401 Unauthorized - try to refresh token
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          
          try {
            console.log('Token expired, attempting refresh...');
            await authService.refreshAuthToken();
            
            // Retry the original request with new token
            const newToken = authService.getAuthToken();
            if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return this.api(originalRequest);
            }
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
            // Redirect to login if refresh fails
            authService.logout();
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
        }
        
        // Log error response for debugging
        console.error('API Response Error:', {
          status: error.response?.status,
          url: error.config?.url,
          data: error.response?.data,
          message: error.message,
        });
        
        return Promise.reject(error);
      }
    );
  }

  /**
   * GET request
   */
  async get<T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<ApiResponse<T>>> {
    try {
      console.log('apiService.get called with:', { url, config });
      
      const requestConfig = autoAddAuthIfRequired(url, config);
      
      // Debug logging in development
      if (process.env.NODE_ENV === 'development') {
        console.log(`API GET: ${url}`);
        console.log('Original config:', config);
        console.log('Request config after auth:', requestConfig);
        console.log('Request config headers:', requestConfig.headers);
      }
      
      const response = await this.api.get<ApiResponse<T>>(url, requestConfig);
      return response;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * POST request
   */
  async post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<ApiResponse<T>>> {
    try {
      const requestConfig = autoAddAuthIfRequired(url, config);
      
      // Debug logging in development
      if (process.env.NODE_ENV === 'development') {
        console.log(`API POST: ${url}`);
        console.log('Request config headers:', requestConfig.headers);
      }
      
      const response = await this.api.post<ApiResponse<T>>(url, data, requestConfig);
      return response;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * PUT request
   */
  async put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<ApiResponse<T>>> {
    try {
      const requestConfig = autoAddAuthIfRequired(url, config);
      const response = await this.api.put<ApiResponse<T>>(url, data, requestConfig);
      return response;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * PATCH request
   */
  async patch<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<ApiResponse<T>>> {
    try {
      const requestConfig = autoAddAuthIfRequired(url, config);
      const response = await this.api.patch<ApiResponse<T>>(url, data, requestConfig);
      return response;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * DELETE request
   */
  async delete<T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<ApiResponse<T>>> {
    try {
      const requestConfig = autoAddAuthIfRequired(url, config);
      const response = await this.api.delete<ApiResponse<T>>(url, requestConfig);
      return response;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Upload file with FormData
   */
  async upload<T = any>(
    url: string,
    file: File,
    additionalData?: Record<string, any>,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<ApiResponse<T>>> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      if (additionalData) {
        Object.entries(additionalData).forEach(([key, value]) => {
          formData.append(key, value);
        });
      }

      const requestConfig = autoAddAuthIfRequired(url, config);
      const response = await this.api.post<ApiResponse<T>>(url, formData, {
        ...requestConfig,
        headers: {
          ...requestConfig.headers,
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return response;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Download file
   */
  async download(
    url: string,
    filename?: string,
    config?: AxiosRequestConfig
  ): Promise<void> {
    try {
      const requestConfig = autoAddAuthIfRequired(url, config);
      const response = await this.api.get(url, {
        ...requestConfig,
        responseType: 'blob',
      });
      
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Handle API errors consistently
   */
  private handleError(error: any): ErrorResponse {
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      return {
        message: data?.message || data?.detail || `HTTP ${status} Error`,
        status,
        errors: data?.errors,
      };
    } else if (error.request) {
      // Request was made but no response received
      return {
        message: 'No response from server. Please check your connection.',
        status: 0,
      };
    } else {
      // Something else happened
      return {
        message: error.message || 'An unexpected error occurred',
        status: 0,
      };
    }
  }

  /**
   * Get the underlying axios instance for advanced usage
   */
  getAxiosInstance(): AxiosInstance {
    return this.api;
  }

  /**
   * Set default headers
   */
  setDefaultHeaders(headers: Record<string, string>): void {
    Object.entries(headers).forEach(([key, value]) => {
      this.api.defaults.headers.common[key] = value;
    });
  }

  /**
   * Clear default headers
   */
  clearDefaultHeaders(): void {
    this.api.defaults.headers.common = {};
  }
}

// Export singleton instance
export const apiService = new ApiService();
export default apiService; 